import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { TrailTimeRange } from 'config/trailOptions';

interface Asset {
  id: number;
  gtsId: string;
  statusCodeColor: string;
  name: string;
  lat: number;
  lng: number;
  trail: {
    lat: number;
    lng: number;
    timestamp: number;
  }[];
  visible?: boolean;
}

interface MapState {
  trailsEnabled: boolean;
  activeTrailOption: TrailTimeRange;
  colorTrailsEnabled: boolean;
  colorTrailsMode: 'vehicle' | 'length';

  assets: Asset[];
  hoveredAssetId: number | null;
  selectedAssetId: number | null;

  zoom: number;
  location: {
    lat: number;
    lng: number;
  };
  destination: {
    lat: number;
    lng: number;
  } | null;
  bounds: any;

  jwt: string;
  companyName: string;
  expired: boolean;
}

type MapAction =
  | { type: 'TOGGLE_TRAILS_ENABLED' }
  | { type: 'SET_ACTIVE_TRAIL_OPTION'; payload: TrailTimeRange }
  | { type: 'TOGGLE_COLOR_TRAILS' }
  | { type: 'SET_COLOR_TRAILS_MODE'; payload: 'vehicle' | 'length' }
  | { type: 'SET_ASSETS'; payload: Asset[] }
  | { type: 'UPDATE_ASSET'; payload: Asset }
  | {
      type: 'UPDATE_ASSET_LOCATION';
      payload: { gtsId: string; lat: number; lng: number; timestamp: number; statusCodeColor: string };
    }
  | { type: 'SET_HOVERED_ASSET'; payload: number | null }
  | { type: 'SET_SELECTED_ASSET'; payload: number | null }
  | { type: 'SET_MAP_DETAILS'; payload: Partial<MapState> }
  | { type: 'SET_LOCATION'; payload: { lat: number; lng: number } }
  | { type: 'SET_ZOOM'; payload: number }
  | { type: 'SET_EXPIRED'; payload: boolean }
  | { type: 'TOGGLE_ASSET_VISIBILITY'; payload: number }
  | { type: 'SET_ALL_ASSETS_VISIBILITY'; payload: boolean };

const initialState: MapState = {
  trailsEnabled: false,
  activeTrailOption: TrailTimeRange.ONE_HOUR,
  colorTrailsEnabled: false,
  colorTrailsMode: 'length',
  assets: [],
  hoveredAssetId: null,
  selectedAssetId: null,
  zoom: 5,
  location: {
    lat: 38.6406812,
    lng: -106.1094301,
  },
  destination: null,
  bounds: null,
  jwt: '',
  companyName: '',
  expired: false,
};

const mapReducer = (state: MapState, action: MapAction): MapState => {
  switch (action.type) {
    case 'TOGGLE_TRAILS_ENABLED':
      return { ...state, trailsEnabled: !state.trailsEnabled };

    case 'SET_ACTIVE_TRAIL_OPTION':
      return { ...state, activeTrailOption: action.payload };

    case 'TOGGLE_COLOR_TRAILS':
      return { ...state, colorTrailsEnabled: !state.colorTrailsEnabled };

    case 'SET_COLOR_TRAILS_MODE':
      return { ...state, colorTrailsMode: action.payload };

    case 'SET_ASSETS':
      return { 
        ...state, 
        assets: action.payload.map(asset => ({ 
          ...asset, 
          visible: asset.visible !== undefined ? asset.visible : true 
        })) 
      };

    case 'UPDATE_ASSET':
      return {
        ...state,
        assets: state.assets.map((asset) => (asset.id === action.payload.id ? action.payload : asset)),
      };

    case 'UPDATE_ASSET_LOCATION':
      return {
        ...state,
        assets: state.assets.map((asset) =>
          asset.gtsId === action.payload.gtsId
            ? {
                ...asset,
                lat: action.payload.lat,
                lng: action.payload.lng,
                statusCodeColor: action.payload.statusCodeColor,
                trail: [
                  ...asset.trail,
                  {
                    lat: action.payload.lat,
                    lng: action.payload.lng,
                    timestamp: action.payload.timestamp,
                  },
                ],
              }
            : asset
        ),
      };

    case 'SET_HOVERED_ASSET':
      return { ...state, hoveredAssetId: action.payload };

    case 'SET_SELECTED_ASSET':
      return { ...state, selectedAssetId: action.payload };

    case 'SET_MAP_DETAILS':
      const payload = { ...action.payload };
      if (payload.assets) {
        payload.assets = payload.assets.map(asset => ({ 
          ...asset, 
          visible: asset.visible !== undefined ? asset.visible : true 
        }));
      }
      return { ...state, ...payload };

    case 'SET_LOCATION':
      return { ...state, location: action.payload };

    case 'SET_ZOOM':
      return { ...state, zoom: action.payload };

    case 'SET_EXPIRED':
      return { ...state, expired: action.payload };

    case 'TOGGLE_ASSET_VISIBILITY':
      return {
        ...state,
        assets: state.assets.map((asset) =>
          asset.id === action.payload ? { ...asset, visible: !asset.visible } : asset
        ),
      };

    case 'SET_ALL_ASSETS_VISIBILITY':
      return {
        ...state,
        assets: state.assets.map((asset) => ({ ...asset, visible: action.payload })),
      };

    default:
      return state;
  }
};

interface MapContextType {
  state: MapState;
  assets: Asset[];
  toggleTrailsEnabled: () => void;
  setActiveTrailOption: (value: TrailTimeRange) => void;
  toggleColorTrails: () => void;
  setColorTrailsMode: (mode: 'vehicle' | 'length') => void;
  setAssets: (assets: Asset[]) => void;
  updateAsset: (asset: Asset) => void;
  updateAssetLocation: (gtsId: string, lat: number, lng: number, timestamp: number, statusCodeColor: string) => void;
  setHoveredAsset: (id: number | null) => void;
  setSelectedAsset: (id: number | null) => void;
  setMapDetails: (details: Partial<MapState>) => void;
  setLocation: (location: { lat: number; lng: number }) => void;
  setZoom: (zoom: number) => void;
  setExpired: (expired: boolean) => void;
  toggleAssetVisibility: (assetId: number) => void;
  setAllAssetsVisibility: (visible: boolean) => void;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

interface MapProviderProps {
  children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(mapReducer, initialState);

  const toggleTrailsEnabled = () => {
    dispatch({ type: 'TOGGLE_TRAILS_ENABLED' });
  };

  const setActiveTrailOption = (value: TrailTimeRange) => {
    dispatch({ type: 'SET_ACTIVE_TRAIL_OPTION', payload: value });
  };

  const toggleColorTrails = () => {
    dispatch({ type: 'TOGGLE_COLOR_TRAILS' });
  };

  const setColorTrailsMode = (mode: 'vehicle' | 'length') => {
    dispatch({ type: 'SET_COLOR_TRAILS_MODE', payload: mode });
  };

  const setAssets = (assets: Asset[]) => {
    dispatch({ type: 'SET_ASSETS', payload: assets });
  };

  const updateAsset = (asset: Asset) => {
    dispatch({ type: 'UPDATE_ASSET', payload: asset });
  };

  const updateAssetLocation = (gtsId: string, lat: number, lng: number, timestamp: number, statusCodeColor: string) => {
    dispatch({ type: 'UPDATE_ASSET_LOCATION', payload: { gtsId, lat, lng, timestamp, statusCodeColor } });
  };

  const setHoveredAsset = (id: number | null) => {
    dispatch({ type: 'SET_HOVERED_ASSET', payload: id });
  };

  const setSelectedAsset = (id: number | null) => {
    dispatch({ type: 'SET_SELECTED_ASSET', payload: id });
  };

  const setMapDetails = (details: Partial<MapState>) => {
    dispatch({ type: 'SET_MAP_DETAILS', payload: details });
  };

  const setLocation = (location: { lat: number; lng: number }) => {
    dispatch({ type: 'SET_LOCATION', payload: location });
  };

  const setZoom = (zoom: number) => {
    dispatch({ type: 'SET_ZOOM', payload: zoom });
  };

  const setExpired = (expired: boolean) => {
    dispatch({ type: 'SET_EXPIRED', payload: expired });
  };

  const toggleAssetVisibility = (assetId: number) => {
    dispatch({ type: 'TOGGLE_ASSET_VISIBILITY', payload: assetId });
  };

  const setAllAssetsVisibility = (visible: boolean) => {
    dispatch({ type: 'SET_ALL_ASSETS_VISIBILITY', payload: visible });
  };

  return (
    <MapContext.Provider
      value={{
        state,
        assets: state.assets,
        toggleTrailsEnabled,
        setActiveTrailOption,
        toggleColorTrails,
        setColorTrailsMode,
        setAssets,
        updateAsset,
        updateAssetLocation,
        setHoveredAsset,
        setSelectedAsset,
        setMapDetails,
        setLocation,
        setZoom,
        setExpired,
        toggleAssetVisibility,
        setAllAssetsVisibility,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};

export const useMap = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};
